import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, updateDoc } from 'firebase/firestore';

function AdminMenuManagement() {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editedItemName, setEditedItemName] = useState('');
  const [editedItemDescription, setEditedItemDescription] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch sedes on component mount
  useEffect(() => {
    const fetchSedes = async () => {
      const sedesCollection = collection(db, 'menuSedes');
      const sedesSnapshot = await getDocs(sedesCollection);
      const sedesList = sedesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSedes(sedesList);
      setLoading(false);
    };
    fetchSedes();
  }, []);

  // Fetch categories when selectedSede changes
  useEffect(() => {
    const fetchCategories = async () => {
      if (selectedSede) {
        const categoriesCollection = collection(db, 'menuSedes', selectedSede.id, 'categories');
        const categoriesSnapshot = await getDocs(query(categoriesCollection, orderBy('name')));
        const categoriesList = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setCategories(categoriesList);
        setSelectedCategory(null); // Reset selected category when sede changes
        setItems([]); // Clear items
      }
    };
    fetchCategories();
  }, [selectedSede]);

  // Fetch items when selectedCategory changes
  useEffect(() => {
    const fetchItems = async () => {
      if (selectedSede && selectedCategory) {
        const itemsCollection = collection(db, 'menuSedes', selectedSede.id, 'categories', selectedCategory.id, 'items');
        const itemsSnapshot = await getDocs(query(itemsCollection, orderBy('name')));
        const itemsList = itemsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setItems(itemsList);
      }
    };
    fetchItems();
  }, [selectedSede, selectedCategory]);

  const handleAddCategory = async () => {
    if (newCategoryName.trim() === '' || !selectedSede) return;
    try {
      const categoriesCollection = collection(db, 'menuSedes', selectedSede.id, 'categories');
      const docRef = await addDoc(categoriesCollection, { name: newCategoryName });
      setCategories([...categories, { id: docRef.id, name: newCategoryName }]);
      setNewCategoryName('');
    } catch (error) {
      console.error("Error adding category: ", error);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditedCategoryName(category.name);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || editedCategoryName.trim() === '') return;
    try {
      const categoryRef = doc(db, 'menuSedes', selectedSede.id, 'categories', editingCategory.id);
      await updateDoc(categoryRef, { name: editedCategoryName });
      setCategories(categories.map(cat =>
        cat.id === editingCategory.id ? { ...cat, name: editedCategoryName } : cat
      ));
      setEditingCategory(null);
      setEditedCategoryName('');
    } catch (error) {
      console.error("Error updating category: ", error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!selectedSede) return;
    try {
      await deleteDoc(doc(db, 'menuSedes', selectedSede.id, 'categories', categoryId));
      setCategories(categories.filter(cat => cat.id !== categoryId));
      if (selectedCategory && selectedCategory.id === categoryId) {
        setSelectedCategory(null);
        setItems([]);
      }
    } catch (error) {
      console.error("Error deleting category: ", error);
    }
  };

  const handleAddItem = async () => {
    if (newItemName.trim() === '' || !selectedSede || !selectedCategory) return;
    try {
      const itemsCollection = collection(db, 'menuSedes', selectedSede.id, 'categories', selectedCategory.id, 'items');
      const docRef = await addDoc(itemsCollection, { name: newItemName, description: newItemDescription });
      setItems([...items, { id: docRef.id, name: newItemName, description: newItemDescription }]);
      setNewItemName('');
      setNewItemDescription('');
    } catch (error) {
      console.error("Error adding item: ", error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditedItemName(item.name);
    setEditedItemDescription(item.description);
  };

  const handleUpdateItem = async () => {
    if (!editingItem || editedItemName.trim() === '') return;
    try {
      const itemRef = doc(db, 'menuSedes', selectedSede.id, 'categories', selectedCategory.id, 'items', editingItem.id);
      await updateDoc(itemRef, { name: editedItemName, description: editedItemDescription });
      setItems(items.map(item =>
        item.id === editingItem.id ? { ...item, name: editedItemName, description: editedItemDescription } : item
      ));
      setEditingItem(null);
      setEditedItemName('');
      setEditedItemDescription('');
    } catch (error) {
      console.error("Error updating item: ", error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!selectedSede || !selectedCategory) return;
    try {
      await deleteDoc(doc(db, 'menuSedes', selectedSede.id, 'categories', selectedCategory.id, 'items', itemId));
      setItems(items.filter(item => item.id !== itemId));
    } catch (error) {
      console.error("Error deleting item: ", error);
    }
  };

  if (loading) {
    return <p>Cargando administración del menú...</p>;
  }

  return (
    <div>
      <h2>Administrar Menú por Sede</h2>

      {/* Sede Selection */}
      <h3>Seleccionar Sede:</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        {sedes.map(sede => (
          <button
            key={sede.id}
            onClick={() => setSelectedSede(sede)}
            style={{
              padding: '10px 15px',
              backgroundColor: selectedSede?.id === sede.id ? '#28a745' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            {sede.name}
          </button>
        ))}
      </div>

      {selectedSede && (
        <>
          {/* Category Management */}
          <h3>Categorías para {selectedSede.name}:</h3>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Nueva categoría"
              style={{ marginRight: '10px', padding: '8px' }}
            />
            <button onClick={handleAddCategory} style={{ padding: '8px 12px' }}>Agregar Categoría</button>
          </div>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {categories.map(category => (
              <li key={category.id} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                {editingCategory?.id === category.id ? (
                  <>
                    <input
                      type="text"
                      value={editedCategoryName}
                      onChange={(e) => setEditedCategoryName(e.target.value)}
                      style={{ marginRight: '10px', padding: '8px' }}
                    />
                    <button onClick={handleUpdateCategory} style={{ padding: '8px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', marginRight: '5px' }}>Guardar</button>
                    <button onClick={() => setEditingCategory(null)} style={{ padding: '8px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>Cancelar</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectedCategory(category)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: selectedCategory?.id === category.id ? '#17a2b8' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginRight: '10px'
                      }}
                    >
                      {category.name}
                    </button>
                    <button onClick={() => handleEditCategory(category)} style={{ padding: '4px 8px', backgroundColor: '#ffc107', color: 'white', border: 'none', borderRadius: '4px', marginRight: '5px' }}>Editar</button>
                    <button onClick={() => handleDeleteCategory(category.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}>Eliminar</button>
                  </>
                )}
              </li>
            ))}
          </ul>

          {selectedCategory && (
            <>
              {/* Item Management */}
              <h3>Platos para {selectedCategory.name} en {selectedSede.name}:</h3>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Nombre del plato"
                  style={{ marginRight: '10px', padding: '8px' }}
                />
                <input
                  type="text"
                  value={newItemDescription}
                  onChange={(e) => setNewItemDescription(e.target.value)}
                  placeholder="Descripción del plato"
                  style={{ marginRight: '10px', padding: '8px' }}
                />
                <button onClick={handleAddItem} style={{ padding: '8px 12px' }}>Agregar Plato</button>
              </div>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {items.map(item => (
                  <li key={item.id} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    {editingItem?.id === item.id ? (
                      <>
                        <input
                          type="text"
                          value={editedItemName}
                          onChange={(e) => setEditedItemName(e.target.value)}
                          placeholder="Nombre del plato"
                          style={{ marginRight: '10px', padding: '8px' }}
                        />
                        <input
                          type="text"
                          value={editedItemDescription}
                          onChange={(e) => setEditedItemDescription(e.target.value)}
                          placeholder="Descripción del plato"
                          style={{ marginRight: '10px', padding: '8px' }}
                        />
                        <button onClick={handleUpdateItem} style={{ padding: '8px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', marginRight: '5px' }}>Guardar</button>
                        <button onClick={() => setEditingItem(null)} style={{ padding: '8px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        {item.name} - {item.description}
                        <button onClick={() => handleEditItem(item)} style={{ marginLeft: '10px', padding: '4px 8px', backgroundColor: '#ffc107', color: 'white', border: 'none', borderRadius: '4px', marginRight: '5px' }}>Editar</button>
                        <button onClick={() => handleDeleteItem(item.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}>Eliminar</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AdminMenuManagement;
